import { useEffect, useState } from 'react';

/**
 * 한 일정 항목의 시각적 상태.
 *
 *  - `'past'` : 이미 종료된 방송. 카드는 흐리게 표시된다.
 *  - `'live'` : 현재 진행 중. LIVE 뱃지가 펄싱한다.
 *  - `'next'` : 가장 빨리 시작될 다음 방송. UP NEXT 뱃지가 붙는다.
 *  - `'upcoming'` : 그 외 미래 일정. 일반 "대기" 뱃지.
 */
export type ScheduleStatus = 'past' | 'live' | 'next' | 'upcoming';

/**
 * 1분 간격으로 현재 시각을 갱신하는 훅.
 *
 * 컴포넌트가 마운트되어 있는 동안만 동작하며, 언마운트 시 인터벌을 즉시 정리한다.
 * 서버사이드 렌더링 환경(현재 프로젝트는 SPA지만 안전 장치로)에서도 깨지지 않도록
 * `typeof window` 체크를 거친다.
 *
 * @returns 매 분마다 갱신되는 `Date` 인스턴스
 */
export function useNow(): Date {
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    // 다음 정각(분) 경계에 맞춰 첫 갱신을 트리거하면 표시가 일관된다.
    const msToNextMinute = 60_000 - (Date.now() % 60_000);
    const initialTimer = window.setTimeout(() => {
      setNow(new Date());
    }, msToNextMinute);

    const intervalTimer = window.setInterval(() => {
      setNow(new Date());
    }, 60_000);

    return () => {
      window.clearTimeout(initialTimer);
      window.clearInterval(intervalTimer);
    };
  }, []);

  return now;
}

interface ScheduleEntry {
  startAt: string;
  endAt: string;
}

/**
 * 단일 일정 항목 하나의 상태를 계산.
 *
 * @param entry - 시작/종료 ISO 시각만 들어 있어도 충분
 * @param now - 비교 기준 시각
 * @param isEarliestUpcoming - 외부에서 미리 계산한 "가장 빨리 시작될 다음 일정인가" 플래그
 */
export function statusForEntry(
  entry: ScheduleEntry,
  now: Date,
  isEarliestUpcoming: boolean,
): ScheduleStatus {
  const start = new Date(entry.startAt).getTime();
  const end = new Date(entry.endAt).getTime();
  const t = now.getTime();

  if (t >= end) {
    return 'past';
  }
  if (t >= start) {
    return 'live';
  }
  return isEarliestUpcoming ? 'next' : 'upcoming';
}

/**
 * 일정 목록 전체에 대해 각 항목의 상태를 한 번에 계산하고,
 * 그중 "가장 빨리 시작될 다음 항목"을 자동으로 식별한다.
 *
 * 입력 일정의 순서는 보존된다.
 */
export function useScheduleStatuses<T extends ScheduleEntry>(items: readonly T[]): ScheduleStatus[] {
  const now = useNow();

  // 시작 시각이 미래이고 가장 가까운 일정의 인덱스를 찾는다 (UP NEXT 표시용).
  const t = now.getTime();
  let earliestIdx = -1;
  let earliestStart = Number.POSITIVE_INFINITY;
  items.forEach((item, idx) => {
    const start = new Date(item.startAt).getTime();
    const end = new Date(item.endAt).getTime();
    if (start > t && t < end && start < earliestStart) {
      earliestStart = start;
      earliestIdx = idx;
    }
  });

  return items.map((item, idx) => statusForEntry(item, now, idx === earliestIdx));
}
